import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { learners } from '../../data/learners';
import { trainingCampaigns } from '../../data/trainingCampaigns';
import { phishingSimulations } from '../../data/phishingSimulations';
import { getStatusColor, getRiskLevel } from '../../data/helpers';

const { FiUsers, FiBookOpen, FiTarget, FiBell, FiCheck, FiAlertTriangle, FiTrendingUp } = FiIcons;

const completionMap = {
  L001: { TC001: 100, TC003: 100 },
  L002: { TC001: 40, TC002: 100 },
  L003: { TC001: 100, TC005: 100 },
  L004: { TC001: 55, TC002: 100 },
  L005: { TC001: 70, TC003: 60, TC005: 100 },
  L006: { TC001: 100, TC003: 100 },
  L007: { TC004: 0 },
  L008: { TC001: 30, TC002: 100 },
};

const ManagerPortal = () => {
  const { currentUser } = useAuth();
  const [reminded, setReminded] = useState([]);
  const [tab, setTab] = useState('team');

  const teamIds = currentUser.directReports || [];
  const team = learners.filter(l => teamIds.includes(l.id));
  const teamCampaigns = trainingCampaigns.filter(c => c.status === 'Active');

  const overdue = team.filter(l => l.trainingStatus === 'Overdue').length;
  const compliant = team.filter(l => l.trainingStatus === 'Compliant').length;
  const avgRisk = team.length ? Math.round(team.reduce((a, b) => a + b.phishingRiskScore, 0) / team.length) : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Manager Dashboard</h2>
        <p className="text-sm text-slate-500">Your team overview — {team.length} direct reports</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Team Size', value: team.length, color: 'text-blue-600', border: 'border-blue-100', bg: 'bg-blue-50' },
          { label: 'Compliant', value: compliant, color: 'text-emerald-600', border: 'border-emerald-100', bg: 'bg-emerald-50' },
          { label: 'Overdue', value: overdue, color: 'text-red-600', border: 'border-red-100', bg: 'bg-red-50' },
          { label: 'Avg. Risk Score', value: avgRisk, color: avgRisk >= 50 ? 'text-red-600' : avgRisk >= 20 ? 'text-amber-600' : 'text-emerald-600', border: 'border-violet-100', bg: 'bg-violet-50' },
        ].map(s => (
          <div key={s.label} className={`bg-white rounded-xl border ${s.border} shadow-sm p-5`}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{s.label}</p>
            <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-1.5 bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm w-fit">
        {[{ id: 'team', label: 'Team Training', icon: FiUsers }, { id: 'phishing', label: 'Phishing Performance', icon: FiTarget }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}>
            <SafeIcon icon={t.icon} className="text-sm" />{t.label}
          </button>
        ))}
      </div>

      {tab === 'team' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700">Team Training Status</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="p-4">Team Member</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Risk Score</th>
                  <th className="p-4">Active Campaign</th>
                  <th className="p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {team.map(l => {
                  const pct = teamCampaigns.length ? (completionMap[l.id]?.[teamCampaigns[0].id] ?? 0) : 0;
                  const risk = getRiskLevel(l.phishingRiskScore);
                  return (
                    <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {l.firstName[0]}{l.lastName[0]}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{l.firstName} {l.lastName}</p>
                            <p className="text-xs text-slate-400">{l.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(l.trainingStatus)}`}>
                          {l.trainingStatus}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${risk.color}`}>
                          {l.phishingRiskScore} · {risk.label}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-slate-100 rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-red-400'}`}
                              style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-slate-600">{pct}%</span>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <button onClick={() => setReminded(r => r.includes(l.id) ? r : [...r, l.id])}
                          className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${reminded.includes(l.id) ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200'}`}>
                          {reminded.includes(l.id) ? '✓ Reminded' : <span className="flex items-center gap-1"><SafeIcon icon={FiBell} />Send Reminder</span>}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'phishing' && (
        <div className="space-y-4">
          {phishingSimulations.slice(0, 3).map(sim => (
            <div key={sim.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">{sim.simulationName}</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Sent: {sim.sendDate} · {sim.status}</p>
                </div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusColor(sim.status)}`}>{sim.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Click Rate', value: `${sim.clickRate}%`, color: sim.clickRate > 15 ? 'text-red-600' : 'text-amber-600' },
                  { label: 'Report Rate', value: `${sim.reportRate}%`, color: 'text-emerald-600' },
                  { label: 'Sent', value: sim.sent, color: 'text-slate-700' },
                ].map(s => (
                  <div key={s.label} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                    <p className="text-xs text-slate-500">{s.label}</p>
                    <p className={`text-lg font-bold mt-0.5 ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default ManagerPortal;